import { useState, useEffect } from "react";
import { collection, onSnapshot, updateDoc, doc, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { addLog } from "../../utils/audit";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";

export default function DisposedAssets() {
  const [disposedAssets, setDisposedAssets] = useState([]);
  const [user] = useAuthState(auth);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [restoringId, setRestoringId] = useState(null);
  const [hiddenAssetId, setHiddenAssetId] = useState(sessionStorage.getItem('hiddenDisposedAsset') || null);

  const categoryOptions = [
    { value: "ict-equipment", label: "ICT Equipment" },
    { value: "devices", label: "Devices" },
    { value: "laboratory-equipment", label: "Laboratory Equipment" },
  ];

  useEffect(() => {
    const q = collection(db, "disposed_assets");
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        disposalDate: d.data().disposalDate ? new Date(d.data().disposalDate) : new Date(),
      }));
      data.sort((a, b) => b.disposalDate - a.disposalDate); // Most recent first
      setDisposedAssets(data);
    });

    return () => unsubscribe();
  }, []);

  const handleRestoreAsset = async (assetId, assetName) => {
    if (!window.confirm(`Restore "${assetName}" to active assets?`)) return;

    setRestoringId(assetId);
    try {
      const disposedAsset = disposedAssets.find(a => a.id === assetId);
      if (!disposedAsset) return;

      // Create clean asset record for restoration
      const receiveDate = disposedAsset.receiveDate?.toDate ? disposedAsset.receiveDate.toDate() : new Date(disposedAsset.receiveDate || Date.now());
      const newDepreciationDate = new Date(receiveDate);
      newDepreciationDate.setFullYear(receiveDate.getFullYear() + 10); // Set 10 years in future to prevent immediate depreciation

      const assetData = {
        name: disposedAsset.name || "",
        description: disposedAsset.description || "",
        serialNumber: disposedAsset.serialNumber || "",
        category: disposedAsset.category || "",
        categoryLabel: disposedAsset.categoryLabel || "",
        building: disposedAsset.building || "",
        room: disposedAsset.room || "",
        location: disposedAsset.location || "",
        status: "available", // Reset status
        receiveDate: disposedAsset.receiveDate || "",
        depreciationDate: newDepreciationDate.toISOString().split("T")[0], // Future date
        cost: disposedAsset.cost || 0,
        salvageValue: disposedAsset.salvageValue || 0,
        usefulLife: disposedAsset.usefulLife || 5,
        annualDepreciation: disposedAsset.annualDepreciation || 0,
        bookValue: disposedAsset.bookValue || 0,
        qrUrl: disposedAsset.qrUrl || "",
        qrLink: disposedAsset.qrLink || "",
        createdAt: disposedAsset.createdAt || new Date(),
        createdBy: disposedAsset.createdBy || "",
        lastUpdated: new Date(),
      };

      // Remove from disposed_assets first
      await deleteDoc(doc(db, "disposed_assets", assetId));

      // Mark as hidden to prevent reappearance on navigation
      setHiddenAssetId(assetId);
      sessionStorage.setItem('hiddenDisposedAsset', assetId);

      // Immediately update UI to remove the asset
      setDisposedAssets(prev => prev.filter(a => a.id !== assetId));

      // Add back to assets collection
      await addDoc(collection(db, "assets"), assetData);

      await addLog(user?.uid, `Restored disposed asset: ${assetName}`, { assetId });
      alert(`✅ Asset "${assetName}" restored to active list.`);
    } catch (err) {
      alert("Failed to restore asset: " + err.message);
    } finally {
      setRestoringId(null);
    }
  };

  const exportToCSV = () => {
    const headers = ["Name", "Category", "Building", "Room", "Cost", "Book Value", "Disposal Date", "Disposal Reason"];
    const csvData = filteredAssets.map(asset => [
      asset.name,
      asset.categoryLabel || asset.category,
      asset.building,
      asset.room,
      asset.cost || "",
      asset.bookValue?.toFixed(2) || "",
      asset.disposalDate.toLocaleDateString(),
      asset.disposalReason
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `disposed_assets_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredAssets = disposedAssets.filter((asset) => {
    // Hide recently restored assets to prevent reappearance on navigation
    if (asset.id === hiddenAssetId) return false;

    const matchesSearch =
      asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.disposalReason?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === "all" || asset.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Disposed Assets Archive</h2>
          <p className="text-gray-600 mt-1">View and manage disposed or archived assets.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
          >
            Export CSV
          </button>
          <div className="text-sm text-gray-500">
            Total: {disposedAssets.length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, description, or disposal reason..."
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              {categoryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <div className="text-sm text-gray-500">
              Showing: {filteredAssets.length} of {disposedAssets.length}
            </div>
          </div>
        </div>
      </div>

      {/* Asset List */}
      <div className="space-y-4">
        {filteredAssets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No disposed assets found</h3>
          </div>
        ) : (
          filteredAssets.map((asset) => (
            <div
              key={asset.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex flex-col lg:flex-row justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900">{asset.name}</h3>
                  <p className="text-sm text-gray-600">{asset.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3 text-sm text-gray-700">
                    <p>
                      <span className="font-medium">Category:</span>{" "}
                      {asset.categoryLabel || asset.category}
                    </p>
                    <p>
                      <span className="font-medium">Location:</span>{" "}
                      {asset.building} - {asset.room}
                    </p>
                    <p>
                      <span className="font-medium">Cost:</span>{" "}
                      ₱{asset.cost?.toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium">Book Value:</span>{" "}
                      ₱{asset.bookValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm text-gray-700">
                    <p>
                      <span className="font-medium">Disposal Date:</span>{" "}
                      {asset.disposalDate.toLocaleDateString()}
                    </p>
                    <p>
                      <span className="font-medium">Disposal Reason:</span>{" "}
                      {asset.disposalReason}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 lg:mt-0 lg:ml-4">
                  <button
                    onClick={() => handleRestoreAsset(asset.id, asset.name)}
                    disabled={restoringId === asset.id}
                    className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {restoringId === asset.id ? "Restoring..." : "Restore to Active"}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
